import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  FileCode, 
  Play, 
  Download, 
  Copy, 
  RefreshCw,
  AlertCircle,
  FileCheck,
  Maximize2,
  Minimize2,
  Zap
} from 'lucide-react';

const LaTeXEditorModal = ({ isOpen, onClose, initialLatex, materialId, token, onCompileSuccess, onSaveSuccess, isOriginallyLatex }) => {
  const [latex, setLatex] = useState(initialLatex || '');
  const [isCompiling, setIsCompiling] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isProcessingLocal, setIsProcessingLocal] = useState(false);

  useEffect(() => {
    if (initialLatex) setLatex(initialLatex);
  }, [initialLatex]);

  useEffect(() => {
    if (isOpen && !latex && !initialLatex && materialId && !isOriginallyLatex) {
      handleGenerate(false);
    }
  }, [isOpen, initialLatex, isOriginallyLatex, materialId]);

  const handleGenerate = async (force = false, method = 'auto') => {
    if (method === 'marker') setIsProcessingLocal(true);
    setIsGenerating(true);
    setError(null);
    try {
      if (method === 'marker') {
        // === Async job pattern to avoid proxy timeout ===
        // 1. Start the job (returns immediately with a jobId)
        const startRes = await fetch('/api/latex/generate/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ id: materialId })
        });
        const startData = await startRes.json();
        if (!startRes.ok) throw new Error(startData.error || 'Failed to start scan');
        const { jobId } = startData;

        // 2. Poll every 3 seconds until done or error
        await new Promise((resolve, reject) => {
          // Safety: absolute 10-minute timeout
          const timeout = setTimeout(() => {
            clearInterval(interval);
            reject(new Error('Scan timed out after 10 minutes. The server may still be processing.'));
          }, 10 * 60 * 1000);

          const interval = setInterval(async () => {
            try {
              const statusRes = await fetch(`/api/latex/generate/status/${jobId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (statusRes.status === 404) {
                clearInterval(interval);
                clearTimeout(timeout);
                reject(new Error('Job lost (server may have restarted). Please try again.'));
                return;
              }

              const statusData = await statusRes.json();
              if (statusData.status === 'done') {
                clearInterval(interval);
                clearTimeout(timeout);
                setLatex(statusData.latex);
                resolve();
              } else if (statusData.status === 'error') {
                clearInterval(interval);
                clearTimeout(timeout);
                reject(new Error(statusData.error || 'Datalab scan failed'));
              }
              // still 'pending' — keep polling
            } catch (pollErr) {
              clearInterval(interval);
              clearTimeout(timeout);
              reject(pollErr);
            }
          }, 3000);
        });


      } else {
        // === Direct fetch for AI/Gemini path ===
        const response = await fetch('/api/latex/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ id: materialId, forceRegenerate: force, method })
        });
        const data = await response.json();
        if (response.ok) {
          setLatex(data.latex);
          if (data.cached) setError({ message: "Loaded from cache. Use 'Regenerate' to call AI again.", type: 'info' });
          else if (data.note) setError({ message: data.note, type: 'info' });
        } else {
          throw new Error(data.error || 'Failed to generate LaTeX');
        }
      }
    } catch (err) {
      setError({ message: err.message, type: 'error' });
    } finally {
      setIsGenerating(false);
      setIsProcessingLocal(false);
    }
  };


  const handleSave = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const response = await fetch('/api/latex/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: materialId, latex })
      });
      const data = await response.json();
      if (response.ok) {
        setError({ message: 'LaTeX code saved successfully.', type: 'info' });
        if (onSaveSuccess) onSaveSuccess(latex);
      } else {
        throw new Error(data.error || 'Failed to save LaTeX');
      }
    } catch (err) {
      setError({ message: err.message, type: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCompile = async () => {
    setIsCompiling(true);
    setError(null);
    setPdfUrl(null);
    try {
      const response = await fetch('/api/latex/compile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ latex, id: materialId })
      });
      const data = await response.json();
      if (response.ok) {
        setPdfUrl(data.url);
        if (onCompileSuccess) onCompileSuccess(data.url);
      } else {
        throw new Error(data.details || data.error || 'Compilation failed');
      }
    } catch (err) {
      setError({ message: err.message, type: 'error' });
    } finally {
      setIsCompiling(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(latex);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={onClose}
        />
        
        <motion.div 
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className={`relative bg-card border border-border shadow-2xl rounded-3xl overflow-hidden flex flex-col transition-all duration-300 ${isFullScreen ? 'w-full h-full' : 'w-full max-w-5xl h-[85vh]'}`}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-accent/10 text-accent">
                <FileCode size={20} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground">LaTeX Editor</h3>
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Convert your notes to professional PDF</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsFullScreen(!isFullScreen)}
                className="p-2 hover:bg-muted rounded-lg text-muted-foreground transition-colors"
                title={isFullScreen ? "Exit Full Screen" : "Full Screen"}
              >
                {isFullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-lg text-muted-foreground transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
            {/* Editor Area */}
            <div className="flex-1 flex flex-col border-r border-border">
              <div className="p-3 bg-muted/20 border-b border-border flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {!isOriginallyLatex && (
                    <>
                      <button 
                        onClick={() => handleGenerate(true, 'marker')}
                        disabled={isGenerating}
                        className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all text-xs font-bold disabled:opacity-50 shadow-md"
                        title="Run heavy-weight local Datalab engine (Best for math/layout)"
                      >
                        <Zap size={14} className={isProcessingLocal ? 'animate-spin' : ''} />
                        {isProcessingLocal ? 'Datalab Scanning...' : 'Datalab Scan'}
                      </button>
                      <button 
                        onClick={() => handleGenerate(true)}
                        disabled={isGenerating}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent/10 text-accent hover:bg-accent/20 transition-all text-xs font-medium disabled:opacity-50"
                      >
                        <RefreshCw size={14} className={isGenerating && !isProcessingLocal ? 'animate-spin' : ''} />
                        {isGenerating && !isProcessingLocal ? 'AI Scanning...' : 'AI Scan'}
                      </button>
                      <div className="w-[1px] h-6 bg-border mx-1" />
                    </>
                  )}
                  <button 
                    onClick={handleSave}
                    disabled={isGenerating}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-all text-xs font-bold disabled:opacity-50"
                  >
                    <FileCheck size={14} />
                    Save Changes
                  </button>
                  <button 
                    onClick={copyToClipboard}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-all text-xs font-bold"
                  >
                    <Copy size={14} />
                    Copy
                  </button>
                </div>
                
                <button 
                  onClick={handleCompile}
                  disabled={isCompiling}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all text-xs font-extrabold shadow-lg shadow-primary/20 disabled:opacity-50"
                >
                  {isCompiling ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                  {isCompiling ? 'Compiling...' : 'Compile PDF'}
                </button>
              </div>
              
              <div className="flex-1 relative">
                <textarea
                  value={latex}
                  onChange={(e) => setLatex(e.target.value)}
                  className="absolute inset-0 w-full h-full p-6 bg-transparent resize-none focus:outline-none text-[13px] font-mono leading-relaxed text-foreground"
                  placeholder="% Type your LaTeX code here..."
                  spellCheck="false"
                />
              </div>
            </div>

            {/* Preview/Error Area */}
            <div className="w-full md:w-80 lg:w-96 flex flex-col bg-muted/5">
              <div className="p-3 bg-muted/20 border-b border-border flex items-center gap-2">
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Output & Status</h4>
              </div>
              
              <div className="flex-1 p-6 overflow-y-auto">
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl flex gap-3 mb-6 ${error.type === 'info' ? 'bg-accent/10 border border-accent/20 text-accent' : 'bg-destructive/10 border border-destructive/20 text-destructive'}`}
                  >
                    <AlertCircle size={18} className="shrink-0" />
                    <div className="flex flex-col gap-1">
                      <span className="text-[11px] font-bold uppercase tracking-tight">{error.type === 'info' ? 'System Message' : 'Error'}</span>
                      <p className="text-xs whitespace-pre-wrap leading-relaxed">{error.message}</p>
                    </div>
                  </motion.div>
                )}

                {isProcessingLocal && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-xl bg-primary/10 border border-primary/20 text-primary mb-6"
                  >
                    <div className="flex gap-3">
                      <RefreshCw size={18} className="shrink-0 animate-spin" />
                      <div className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold uppercase tracking-tight">Local ML Processing</span>
                        <p className="text-xs leading-relaxed">
                          Running <b>Self-Hosted Marker</b> engine. 
                          <br /><br />
                          <span className="opacity-70 italic">
                            NOTE: The <b>first run</b> may take 5–10 minutes to download AI models (several GBs). 
                            Subsequent scans will be much faster.
                          </span>
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {!pdfUrl && !isCompiling && !error && !isProcessingLocal && (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6 opacity-30">
                    <div className="w-16 h-16 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center mb-4">
                      <Play size={24} />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground">Compile your code to see the result</p>
                  </div>
                )}

                {isCompiling && (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6">
                    <div className="relative w-16 h-16 mb-6">
                      <div className="absolute inset-0 border-4 border-muted rounded-full opacity-20" />
                      <div className="absolute inset-0 border-4 border-accent rounded-full border-t-transparent animate-spin" />
                    </div>
                    <p className="text-xs font-bold text-foreground animate-pulse">Running pdflatex...</p>
                    <p className="text-[10px] text-muted-foreground mt-2">This may take a few seconds</p>
                  </div>
                )}

                {pdfUrl && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col gap-6"
                  >
                    <div className="p-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex flex-col items-center text-center">
                      <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
                        <FileCheck size={24} />
                      </div>
                      <h4 className="text-sm font-bold mb-1">Success!</h4>
                      <p className="text-[10px] opacity-80">PDF has been generated successfully</p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <a 
                        href={pdfUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-full py-3 rounded-xl bg-foreground text-background flex items-center justify-center gap-2 text-xs font-bold hover:opacity-90 transition-all"
                      >
                        <Play size={14} />
                        View PDF
                      </a>
                      <a 
                        href={pdfUrl} 
                        download="note_latex.pdf"
                        className="w-full py-3 rounded-xl border border-border flex items-center justify-center gap-2 text-xs font-bold hover:bg-muted transition-all"
                      >
                        <Download size={14} />
                        Download PDF
                      </a>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LaTeXEditorModal;
